// components/ui/PhoneVerification.tsx
// Reusable phone verification component with code input
import React, { useState, useEffect, useRef } from "react";
import {
  View,
  Text,
  TextInput,
  TouchableOpacity,
  StyleSheet,
  ActivityIndicator,
} from "react-native";
import { Feather } from "@expo/vector-icons";
import { useColors, ThemeColors } from "../../hooks/useColors";
import {
  formatPhoneNumberAsUserTypes,
  validatePhoneNumberWithError,
} from "../../utils/phoneValidation";

interface PhoneVerificationProps {
  phoneNumber: string;
  onPhoneChange: (phone: string) => void;
  isVerified: boolean;
  codeSent: boolean;
  isSending: boolean;
  isVerifying: boolean;
  error: string | null;
  onSendCode: () => void;
  onVerifyCode: (code: string) => void;
  onReset?: () => void;
  label?: string;
  placeholder?: string;
  disabled?: boolean;
}

export function PhoneVerification({
  phoneNumber,
  onPhoneChange,
  isVerified,
  codeSent,
  isSending,
  isVerifying,
  error,
  onSendCode,
  onVerifyCode,
  onReset,
  label = "Phone Number",
  placeholder = "(123) 456-7890",
  disabled = false,
}: PhoneVerificationProps) {
  const colors = useColors();
  const styles = createStyles(colors);
  const [code, setCode] = useState("");
  const [phoneError, setPhoneError] = useState("");
  const [countdown, setCountdown] = useState(0);
  const codeInputRef = useRef<TextInput>(null);

  // Countdown timer for resend
  useEffect(() => {
    if (countdown > 0) {
      const timer = setTimeout(() => setCountdown(countdown - 1), 1000);
      return () => clearTimeout(timer);
    }
  }, [countdown]);

  // Focus code input when code is sent
  useEffect(() => {
    if (codeSent && !isVerified && codeInputRef.current) {
      setTimeout(() => codeInputRef.current?.focus(), 100);
    }
  }, [codeSent, isVerified]);

  const handlePhoneChange = (text: string) => {
    const formattedPhone = formatPhoneNumberAsUserTypes(text, phoneNumber);
    
    // Only process if the phone number actually changed
    if (formattedPhone === phoneNumber) {
      return;
    }
    
    onPhoneChange(formattedPhone);
    setPhoneError("");
    
    // Reset verification if phone changes while verification is in progress or completed
    if (isVerified || codeSent) {
      onReset?.();
      setCode("");
    }
  };

  const handleSendCode = () => {
    // Validate phone first
    const validation = validatePhoneNumberWithError(phoneNumber);
    if (!validation.isValid) {
      setPhoneError(validation.error || "Invalid phone number");
      return;
    }

    setPhoneError("");
    setCountdown(60); // 60 second cooldown
    onSendCode();
  };

  const handleCodeChange = (text: string) => {
    // Only allow digits
    const digits = text.replace(/\D/g, "").slice(0, 6);
    setCode(digits);

    // Auto-verify when 6 digits entered
    if (digits.length === 6) {
      onVerifyCode(digits);
    }
  };

  const canSendCode = phoneNumber.length >= 10 && countdown === 0 && !isSending && !isVerifying;

  return (
    <View style={styles.container}>
      {/* Phone Number Input */}
      <View style={styles.inputContainer}>
        <Text style={styles.label}>{label} *</Text>
        <View style={styles.phoneRow}>
          <TextInput
            style={[
              styles.phoneInput,
              (phoneError || error) && styles.inputError,
              isVerified && styles.inputVerified,
              disabled && styles.inputDisabled,
            ]}
            value={phoneNumber}
            onChangeText={handlePhoneChange}
            placeholder={placeholder}
            placeholderTextColor={colors.textLight}
            keyboardType="phone-pad"
            maxLength={14}
            editable={!disabled && !isVerified}
          />
          
          {/* Verified badge with Change button, or Send Code button */}
          {isVerified ? (
            <View style={styles.verifiedActions}>
              <View style={[styles.verifiedBadge]}>
                <Feather name="check-circle" size={16} color={colors.surface} />
                <Text style={styles.verifiedText}>Verified</Text>
              </View>
              <TouchableOpacity
                style={styles.changeButton}
                onPress={() => {
                  onReset?.();
                  setCode("");
                }}
              >
                <Text style={styles.changeButtonText}>Change</Text>
              </TouchableOpacity>
            </View>
          ) : !codeSent ? (
            <TouchableOpacity
              style={[styles.sendButton, !canSendCode && styles.sendButtonDisabled]}
              onPress={handleSendCode}
              disabled={!canSendCode}
            >
              {isSending ? (
                <ActivityIndicator size="small" color="#fff" />
              ) : (
                <Text style={styles.sendButtonText}>
                  {countdown > 0 ? `${countdown}s` : "Verify"}
                </Text>
              )}
            </TouchableOpacity>
          ) : null}
        </View>
        {phoneError && <Text style={styles.errorText}>{phoneError}</Text>}
      </View>

      {/* Verification Code Input */}
      {codeSent && !isVerified && (
        <View style={styles.codeContainer}>
          <Text style={styles.codeLabel}>Enter the 6-digit code sent to your phone</Text>
          <View style={styles.codeRow}>
            <TextInput
              ref={codeInputRef}
              style={[styles.codeInput, error && styles.inputError]}
              value={code}
              onChangeText={handleCodeChange}
              placeholder="000000"
              placeholderTextColor={colors.textLight}
              keyboardType="number-pad"
              maxLength={6}
              autoFocus
            />
            {isVerifying && (
              <ActivityIndicator size="small" color={colors.primary} style={styles.verifyingIndicator} />
            )}
          </View>
          
          {error && <Text style={styles.errorText}>{error}</Text>}
          
          <View style={styles.resendRow}>
            <Text style={styles.resendText}>Didn't receive the code? </Text>
            <TouchableOpacity onPress={handleSendCode} disabled={countdown > 0 || isSending}>
              <Text style={[styles.resendLink, (countdown > 0 || isSending) && styles.resendLinkDisabled]}>
                {countdown > 0 ? `Resend in ${countdown}s` : "Resend"}
              </Text>
            </TouchableOpacity>
          </View>
        </View>
      )}
    </View>
  );
}

const createStyles = (colors: ThemeColors) =>
  StyleSheet.create({
    container: {
      marginBottom: 16,
    },
    inputContainer: {
      marginBottom: 8,
    },
    label: {
      fontSize: 14,
      fontWeight: "500",
      color: colors.text,
      marginBottom: 8,
    },
    phoneRow: {
      flexDirection: "row",
      alignItems: "center",
      gap: 8,
    },
    phoneInput: {
      flex: 1,
      flexShrink: 1,
      minWidth: 0,
      height: 48,
      borderWidth: 1,
      borderColor: colors.border,
      borderRadius: 8,
      paddingHorizontal: 12,
      fontSize: 16,
      color: colors.text,
      backgroundColor: colors.card,
    },
    inputError: {
      borderColor: "#F43F5E", // Rose Red (error)
    },
    inputVerified: {
      borderColor: "#00A86B", // Jade Green
      backgroundColor: colors.card,
    },
    inputDisabled: {
      backgroundColor: colors.background,
      opacity: 0.7,
    },
    sendButton: {
      backgroundColor: colors.primary,
      paddingHorizontal: 16,
      paddingVertical: 12,
      borderRadius: 8,
      minWidth: 80,
      alignItems: "center",
    },
    sendButtonDisabled: {
      backgroundColor: colors.border,
    },
    sendButtonText: {
      color: "#000",
      fontWeight: "600",
      fontSize: 14,
    },
    verifiedActions: {
      flexDirection: "row",
      alignItems: "center",
      gap: 8,
      flexShrink: 0,
    },
    verifiedBadge: {
      flexDirection: "row",
      alignItems: "center",
      backgroundColor: colors.success,
      paddingHorizontal: 12,
      paddingVertical: 8,
      borderRadius: 8,
      gap: 4,
      opacity: 0.6,
    },
    verifiedText: {
      color: colors.surface,
      fontWeight: "600",
      fontSize: 14,
    },
    changeButton: {
      paddingHorizontal: 12,
      paddingVertical: 8,
      borderRadius: 8,
      borderWidth: 1,
      borderColor: colors.border,
      backgroundColor: colors.card,
    },
    changeButtonText: {
      color: colors.text,
      fontWeight: "500",
      fontSize: 14,
    },
    errorText: {
      color: "#F43F5E", // Rose Red (error)
      fontSize: 12,
      marginTop: 4,
    },
    codeContainer: {
      marginTop: 16,
      padding: 16,
      backgroundColor: colors.card,
      borderRadius: 12,
      borderWidth: 1,
      borderColor: colors.border,
    },
    codeLabel: {
      fontSize: 14,
      color: colors.textLight,
      marginBottom: 12,
      textAlign: "center",
    },
    codeRow: {
      flexDirection: "row",
      alignItems: "center",
      justifyContent: "center",
    },
    codeInput: {
      height: 56,
      width: 160,
      borderWidth: 2,
      borderColor: colors.primary,
      borderRadius: 12,
      paddingHorizontal: 16,
      fontSize: 24,
      fontWeight: "600",
      color: colors.text,
      backgroundColor: colors.background,
      textAlign: "center",
      letterSpacing: 8,
    },
    verifyingIndicator: {
      marginLeft: 12,
    },
    resendRow: {
      flexDirection: "row",
      justifyContent: "center",
      marginTop: 16,
    },
    resendText: {
      fontSize: 14,
      color: colors.textLight,
    },
    resendLink: {
      fontSize: 14,
      color: colors.primary,
      fontWeight: "600",
    },
    resendLinkDisabled: {
      color: colors.textLight,
    },
  });

